import EmailIcon from '@mui/icons-material/Email';
import AddressIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import { Box, SvgIcon, Stack } from '@mui/material';
import {
    DateField,
    DeleteButton,
    EditButton,
    EmailField,
    FunctionField,
    SelectField,
    ShowButton,
    TextField,
    useRecordContext,
} from 'react-admin';
import { TagsListEdit } from '../tags/TagsListEdit';
import { useLocation } from 'react-router';
import { useConfigurationContext } from '@app/root/ConfigurationContext';
import { AddressLink } from '@app/misc/AddressLink';
import { LabelValueRow } from '@app/misc/LabelValueRow';
import { SectionDivider } from '@app/misc/SectionDivider';
import { getFullName } from '@domain/member';
import { MemberSummary } from '@wwf/sdk';
import { CopyAppLinkButton } from '@app/misc/CopyAppLinkButton';
import { MemberStatusEditor } from './MemberStatusEditor';

export const MemberAside = ({ link = 'edit' }: { link?: 'edit' | 'show' }) => {
    const location = useLocation();
    const { memberGenderLabels } = useConfigurationContext();
    const record = useRecordContext<MemberSummary>();
    if (!record) return null;
    return (
        <Box ml={4}>
            <Box mb={2}>
                {link === 'edit' ? (
                    <Stack
                        direction={'row'}
                        mb={2}
                        flexWrap={'wrap'}
                        justifyContent={'space-between'}
                        gap={1}
                    >
                        <EditButton resource="member" label="Edit Member" />
                        <CopyAppLinkButton type="member" id={record.id} />
                    </Stack>
                ) : (
                    <ShowButton resource="member" label="Show Member" />
                )}
            </Box>

            <SectionDivider title="Personal info" />
            <SelectField
                source="gender"
                choices={memberGenderLabels}
                optionText={choice => (
                    <LabelValueRow
                        icon={
                            <SvgIcon
                                component={choice.icon}
                                color="disabled"
                                fontSize="small"
                            />
                        }
                        value={<span>{choice.label}</span>}
                    />
                )}
                optionValue="value"
            />
            <LabelValueRow
                icon={<EmailIcon color="disabled" fontSize="small" />}
                value={<EmailField source="email" />}
            />
            <LabelValueRow
                icon={<EmailIcon color="disabled" fontSize="small" />}
                value={<EmailField source="emailOrga" />}
            />
            <LabelValueRow
                icon={<PhoneIcon color="disabled" fontSize="small" />}
                value={<TextField source="phoneNumber" />}
            />
            <LabelValueRow
                icon={<AddressIcon color="disabled" fontSize="small" />}
                value={
                    <FunctionField
                        source="address"
                        render={member => {
                            const address = member.address;
                            return (
                                address && (
                                    <AddressLink
                                        address={`${address.street ?? ''}, ${address.postalCode ?? ''} ${address.city ?? ''}, ${address.country ?? ''}`}
                                    />
                                )
                            );
                        }}
                    />
                }
            />

            <SectionDivider title="Status & Tags" />
            <MemberStatusEditor />
            <TagsListEdit />

            <SectionDivider title="Additional info" />
            <LabelValueRow
                label="Last activity on"
                value={
                    <DateField
                        source="lastSeen"
                        options={{
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                        }}
                    />
                }
            />
            <LabelValueRow
                label="Updated on"
                value={
                    <DateField
                        source="updatedAt"
                        options={{
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                        }}
                    />
                }
            />
            <LabelValueRow
                label="Added on"
                value={
                    <DateField
                        source="createdAt"
                        options={{
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                        }}
                    />
                }
            />
            <LabelValueRow
                label="Member since"
                value={
                    <DateField
                        source="sinceDate"
                        options={{
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                        }}
                    />
                }
            />

            <SectionDivider title="Danger zone" />
            <DeleteButton
                resource="member"
                sx={{ paddingTop: 0.5 }}
                redirect={location.state?.from || undefined}
                mutationMode="pessimistic"
                confirmTitle={`Are you sure you want to delete ${getFullName(record)}?`}
                confirmContent="This action cannot be undone."
                confirmColor="warning"
            />
        </Box>
    );
};
